import { useState, useCallback } from "react";

import { useMutation, useQuery } from "@apollo/client";
import { Select } from "antd";
import { clsx } from "clsx";

import { RVHighlightedString, Tag as TagType } from "@regal-voice/ui";
import { Column, Row } from "Components/shared/Flexbox";
import { renderErrorMessage } from "Services/LoggingService";
import { createNewTag } from "Services/marketing-api/tags/mutations";
import { getTags } from "Services/marketing-api/tags/queries";

import type { SelectProps } from "antd";

import styles from "./TagsSelect.module.scss";

export type SelectValueType = {
    label: string;
    value: string;
};

interface TagProps extends Record<string, unknown> {
    data?: SelectValueType[];
}

interface TagsSelectOption {
    name?: string;
    label: React.ReactNode;
    value?: string | number | null;
    children?: Omit<TagsSelectOption, "children">[];
}

interface TagSelectProps extends Omit<SelectProps<SelectValueType[], TagsSelectOption>, "options" | "children"> {
    entityType?: string;
    onTagDeselect?: (tagId: string) => void;
    onTagSelect?: (tag: TagType) => void;
    onCreate?: (props: Record<string, unknown>) => void;
    onTagUpdate?: (tags: TagType[]) => void;
    tags?: TagsSelectOption[];
    hideEmptyDataMessage?: boolean;
}

export type TagSelectComponentProps = TagSelectProps & TagProps;

function tagOptions(tagsData: Record<string, any>) {
    return tagsData?.map((tag: any) => {
        return {
            name: tag.name,
            label: tag.name,
            value: tag.id,
        };
    });
}

function alphabeticalTagsSort(a: TagsSelectOption, b: TagsSelectOption) {
    return ((a?.label ?? "") as string).toLowerCase() > ((b?.label ?? "") as string).toLowerCase() ? 1 : -1;
}

export function TagsSelect({ hideEmptyDataMessage = true, ...props }: TagSelectComponentProps) {
    const { entityType, onCreate: createTag, ...selectProps } = props;
    const [search, setSearch] = useState<string | undefined>();
    const [tags, setTags] = useState<TagsSelectOption[]>([]);

    const canAddTag = !!search && !tags.map((tag) => tag.label).includes(search);

    const clearSearch = useCallback(() => {
        setSearch(undefined);
    }, [setSearch]);

    const { refetch } = useQuery(getTags, {
        variables: { entityType },
        fetchPolicy: "cache-and-network",
        errorPolicy: "all",
        onCompleted(data) {
            setTags(tagOptions(data?.getTags?.items));
        },
        onError(e: any) {
            renderErrorMessage({ content: `Error fetching segments: ${e.message}`, error: e });
        },
    });

    const [createNewTagDB] = useMutation(createNewTag, {
        onCompleted: async (data) => {
            const { id, name } = data?.createTag || {};
            await refetch();
            createTag?.({ id, name });
        },
        onError: (error) => renderErrorMessage({ content: `Creating journey failed: ${error.message}.`, error }),
    });

    const handleSearch = useCallback(
        (value: string) => {
            setSearch(value);
        },
        [setSearch]
    );

    const handleAddTag = useCallback(async () => {
        // todo: find out why this is not called when options are filtered
        if (createTag && search) {
            try {
                await createNewTagDB({ variables: { name: search, entityType } });
            } finally {
                clearSearch();
            }
        }
    }, [clearSearch, createNewTagDB, createTag, entityType, search]);

    return (
        <Select
            data-testid="tags-select"
            mode="multiple"
            onSearch={handleSearch}
            searchValue={search}
            filterOption={filterOption}
            className={clsx(styles.tagSelector, { [styles.hideEmptyDataMessage]: hideEmptyDataMessage })}
            onSelect={clearSearch}
            onDeselect={clearSearch}
            // fixes the dropdown position and resolves weird repositioning issues
            getPopupContainer={(trigger) => trigger.parentNode}
            virtual={false} // Disable virtual scroll because it causes rendering issues after filtering
            dropdownRender={(menu) => {
                return (
                    <Column gap={1}>
                        {menu}
                        {createTag && !!canAddTag && (
                            <div>
                                <Row
                                    data-testid="new-tag-cta"
                                    className={styles.newTagRow}
                                    justifyContent="space-between"
                                    padding={2}
                                    paddingLeft={3}
                                    paddingRight={3}
                                    style={{ zIndex: 100 }}
                                >
                                    <span>{search}</span>
                                    <span className={styles.addTagCTA} onClick={handleAddTag}>
                                        Create Tag{" "}
                                    </span>
                                </Row>
                            </div>
                        )}
                    </Column>
                );
            }}
            allowClear={true}
            {...selectProps}
        >
            {(tags || []).sort(alphabeticalTagsSort).map((option) => (
                <Select.Option key={option.value} value={option.value} name={option.label}>
                    <RVHighlightedString key={option.value} val={option.label as string} highlightVal={search} />
                </Select.Option>
            ))}
        </Select>
    );
}

const filterOption: SelectProps<TagsSelectOption["value"]>["filterOption"] = (
    input: string,
    option: TagsSelectOption | undefined
) => {
    return (option?.name ?? "").toLowerCase().includes(input?.toLowerCase());
};
